// Saving a Pitch Match flicker after the fact (issue #3587). The dot sometimes bounces an octave or
// a fifth away for a few frames and back; seen on a phone, the moment is gone before anyone could
// start a recording. So while a session runs on a dev server the Learn audio session keeps the last
// 30 s of raw microphone audio (learn_mic_capture.ts) and the page keeps the reducer's actions of the
// last 40 s here, and the note screen's "Save last 30 s" uploads both: the audio as a 16-bit WAV and
// the actions, with the session they belong to, as JSON. Replaying those actions through the reducer
// shows what the machine made of every frame the screen was drawn from, and running the detectors
// again over the WAV shows what the phone heard.
//
// The WAV has two channels (issue #3708): the microphone in channel 0 and, in channel 1, the guide
// reference, everything the Learn audio session played, sample for sample with the microphone. A
// moment the guide sounded in can then be replayed through an echo canceller as well as the
// detectors, which read channel 0 alone. Captures saved before this are mono and carry no `channels`.
//
// The uploads go to the audio lab's sinks (server/src/audio_lab_routes.ts), which exist only on dev
// servers, and land in ~/.syp-dev/audio-lab/ on the developer's machine: the WAV with its small
// metadata sidecar, then the session JSON, which names the WAV. The offline replay reads the
// session JSON. The URLs are relative, as the lab's are: a dev server serves the client and the API
// on one origin, and the phone reaches it through a tunnel.
//
// Every action is logged against the note it was scored against, because the log reaches back only
// 40 s and a replay that starts mid-session cannot otherwise tell which target a frame belonged to.

import { midiToNoteName } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { RecordingChannel, recordingChannels } from '../../../audio/guide_reference.ts';
import { encodeWav } from '../../../audio/pitch/wav.ts';
import { LearnAudioSession } from '../audio/learn_audio_session.ts';
import { PitchMode, pitchNoteIndex, PitchSessionAction, PitchSessionState } from './pitch_match_session.ts';

// How far back the action log reaches: a little further than the audio (captureKeepSeconds), so
// every frame analysed from the audio that is saved has its entry
export const captureActionsKeepMs = 40_000;

// One action as the page dispatched it, with the note it was scored against: the latest committed
// state's when it was dispatched
export interface PitchCaptureEntry {
  action: PitchSessionAction;
  noteIndex: number;
  targetMidi: number;
}

// What the session JSON holds; the offline replay reads it. `recording` is the path the server gave
// the WAV, `startTimeMs` is the audio clock at its first sample (the clock every action's own time
// is on), and `noteIndex` and `targetMidi` are the note on screen when the capture was saved.
// `channels` says what each of the WAV's channels is (the shared audio module's recordingChannels,
// which the audio lab's recordings name too); a capture saved before it existed has none, and its WAV
// is the microphone alone.
export interface PitchCaptureSession {
  kind: 'pitch-match-capture';
  savedAt: string;
  label: string;
  recording: string;
  sampleRate: number;
  channels?: RecordingChannel[];
  startTimeMs: number;
  seconds: number;
  level: LearnLevel;
  mode: PitchMode;
  targets: number[];
  noteIndex: number;
  targetMidi: number;
  entries: PitchCaptureEntry[];
}

const captureKind: PitchCaptureSession['kind'] = 'pitch-match-capture';

// The audio-clock time an action carries, if it carries one: a frame's, the time the guide was
// anchored at, and the page's reading of the clock for a tick or a Skip. The taps and failures
// that carry none happened between the timed actions on either side of them.
export function actionTimeMs(action: PitchSessionAction): number | undefined {
  switch (action.type) {
    case 'frame':
      return action.frame.timeMs;
    case 'guideStarted':
      return action.atMs;
    case 'tick':
    case 'skip':
      return action.nowMs;
    case 'replay':
    case 'micFailed':
    case 'retry':
      return undefined;
  }
}

// The log without the entries older than `keepMs` before `nowMs`, dropped from the front only, so
// the order is never touched. An untimed entry is as old as the next timed entry after it, so one
// that sits between two entries past the cutoff goes with them; one just ahead of a recent entry
// stays. Returns the same array when there is nothing to drop.
export function trimCaptureLog(entries: PitchCaptureEntry[], nowMs: number, keepMs: number): PitchCaptureEntry[] {
  const cutoffMs = nowMs - keepMs;
  let keepFrom = 0;
  for (let index = 0; index < entries.length; index += 1) {
    const timeMs = actionTimeMs(entries[index].action);
    if (timeMs === undefined) {
      continue;
    }
    if (timeMs >= cutoffMs) {
      break;
    }
    keepFrom = index + 1;
  }
  return keepFrom === 0 ? entries : entries.slice(keepFrom);
}

// Uploads the kept audio and the actions behind it; undefined when there is no audio to save. The
// audio and the entries are both taken before the first upload starts, so the frames that keep
// arriving while it is in flight are not in the session the WAV is paired with. Throws when either
// upload fails.
export async function savePitchCapture({
  audio,
  state,
  entries,
  fetchFn = (url, init) => fetch(url, init),
  now = () => new Date(),
}: {
  audio: LearnAudioSession;
  state: PitchSessionState;
  entries: PitchCaptureEntry[];
  fetchFn?: typeof fetch;
  now?: () => Date;
}): Promise<{ recordingPath: string; sessionPath: string } | undefined> {
  const capture = audio.takeCapture();
  if (!capture) {
    return undefined;
  }
  const { samples, reference, sampleRate, startTimeMs } = capture;
  const channels = recordingChannels;
  // The actions behind the saved audio; the taps and failures that carry no time are kept
  const entriesInAudio = entries.filter((entry) => (actionTimeMs(entry.action) ?? startTimeMs) >= startTimeMs);
  const seconds = samples.length / sampleRate;
  const { level, mode, targets } = state;
  const targetMidi = state.note.targetMidi;
  const label = `pitch-${midiToNoteName(targetMidi)}-${level}`;

  // The sidecar the recording route stores beside the WAV travels in the query string, so it is
  // kept to a few numbers; everything else is in the session JSON
  const metadata = { kind: captureKind, targetMidi, level, mode, sampleRate, channels, startTimeMs, seconds };
  const query = new URLSearchParams({ label, metadata: JSON.stringify(metadata) });
  const recordingPath = await postForPath(
    fetchFn,
    `/api/dev/audio-lab/recording?${query.toString()}`,
    'audio/wav',
    encodeWav([samples, reference], sampleRate), // in the order `channels` names them
  );

  const session: PitchCaptureSession = {
    kind: captureKind,
    savedAt: now().toISOString(),
    label,
    recording: recordingPath,
    sampleRate,
    channels,
    startTimeMs,
    seconds,
    level,
    mode,
    targets,
    noteIndex: pitchNoteIndex(state),
    targetMidi,
    entries: entriesInAudio,
  };
  const sessionPath = await postForPath(
    fetchFn,
    '/api/dev/audio-lab/session',
    'application/json',
    JSON.stringify(session),
  );
  return { recordingPath, sessionPath };
}

// Both sinks answer with the path they wrote the file to
async function postForPath(
  fetchFn: typeof fetch,
  url: string,
  contentType: string,
  body: ArrayBuffer | string,
): Promise<string> {
  const response = await fetchFn(url, { method: 'POST', headers: { 'Content-Type': contentType }, body });
  if (!response.ok) {
    throw new Error(`upload to ${url.split('?')[0]} failed: ${response.status}`);
  }
  const { path } = (await response.json()) as { path: string };
  return path;
}
